'use client';

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { CartLine } from '@/entities/cart';
import { cartQueryOptions, useAddCartLines } from '@/entities/cart/client';
import type { Product } from '@/entities/product';
import { CartLineRow } from '@/features/cart-line-actions/client';
import { CartRecommendations } from '@/features/cart-recommendations/client';
import { formatMoney } from '@/shared/lib/money';
import { formatCount, GOODS_FORMS } from '@/shared/lib/plural';
import { Breadcrumbs } from '@/shared/ui/breadcrumbs';
import { ButtonLink } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import { StatusScreen } from '@/shared/ui/status-screen';
import { TextButton } from '@/shared/ui/text-link';
import { CartSummary } from '@/widgets/cart-summary';

import styles from './cart-view.module.scss';

type Toast = { text: string; tone: 'ok' | 'error'; undo: { line: CartLine; index: number } | null };

export function CartView({ recommendations }: { recommendations: Product[] }) {
  const cart = useQuery(cartQueryOptions());
  const restore = useAddCartLines();
  const [toast, setToast] = useState<Toast | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const data = cart.data;

  if (!data) {
    return (
      <div data-theme="editorial" className={styles.root}>
        <StatusScreen
          title={cart.isError ? 'Корзина недоступна' : 'Загружаем корзину…'}
          actions={
            cart.isError ? (
              <ButtonLink href="/catalog" variant="ghost">
                Перейти в каталог
              </ButtonLink>
            ) : undefined
          }
        >
          {cart.isError && <p>{cart.error?.message ?? 'Попробуйте обновить страницу.'}</p>}
        </StatusScreen>
      </div>
    );
  }

  const empty = data.lines.length === 0;
  const undo = () => {
    if (!toast?.undo) return;
    const { line, index } = toast.undo;
    restore.mutate(
      { lines: [{ sku: line.sku, quantity: line.quantity, position: index }] },
      {
        onSuccess: () => setToast(null),
        onError: (error) => setToast({ text: error.message, tone: 'error', undo: null }),
      },
    );
  };

  return (
    <div data-theme="editorial" className={styles.root}>
      <section className={styles.main}>
        <Breadcrumbs items={[{ label: 'Главная', href: '/' }, { label: 'Корзина' }]} />
        <div className={styles.heading}>
          <h1 className={styles.title}>Корзина</h1>
          <span className={styles.count}>{formatCount(data.itemsCount, GOODS_FORMS)}</span>
        </div>
        <output className={styles.toast}>
          {toast && (
            <span className={styles['toast-body']} data-tone={toast.tone}>
              <Icon
                name={toast.tone === 'ok' ? 'check' : 'alert'}
                size={16}
                strokeWidth={1.6}
                className={styles['toast-icon']}
              />
              <span>{toast.text}</span>
              {toast.undo && (
                <TextButton onClick={undo} disabled={restore.isPending}>
                  Вернуть
                </TextButton>
              )}
            </span>
          )}
        </output>
        <div className={styles.layout}>
          <div className={styles.list}>
            {empty ? (
              <div className={styles.empty}>
                <h2 className={styles['empty-title']}>В корзине пока пусто</h2>
                <p className={styles['empty-text']}>
                  Загляните в каталог — там вещи для практики, заботы о себе и спокойных вечеров.
                </p>
                <ButtonLink
                  href="/catalog"
                  variant="ghost"
                  size="md"
                  className={styles['empty-cta']}
                >
                  Перейти в каталог
                </ButtonLink>
              </div>
            ) : (
              <ul className={styles.rows}>
                {data.lines.map((line, index) => (
                  <CartLineRow
                    key={line.lineId}
                    line={line}
                    index={index}
                    editing={editingId === line.lineId}
                    onEditingChange={(next) => setEditingId(next ? line.lineId : null)}
                    onRemoved={(removed, at) =>
                      setToast({
                        text: `Товар удалён: ${removed.name}`,
                        tone: 'ok',
                        undo: { line: removed, index: at },
                      })
                    }
                    onError={(message) => setToast({ text: message, tone: 'error', undo: null })}
                    onChanged={() => setToast(null)}
                  />
                ))}
              </ul>
            )}
          </div>
          {!empty && <CartSummary cart={data} />}
        </div>
      </section>
      <div className={styles.recs}>
        <CartRecommendations
          products={recommendations}
          onAdded={(name) =>
            setToast({ text: `Добавлено в корзину: ${name}`, tone: 'ok', undo: null })
          }
        />
      </div>
      {!empty && (
        <div className={styles.bar}>
          <span className={styles['bar-total']}>{formatMoney(data.total)}</span>
          <ButtonLink href="/checkout" size="md">
            Оформить
          </ButtonLink>
        </div>
      )}
    </div>
  );
}
